import { onFID } from 'web-vitals';

/**
 * Initializes the JS profiler when supported and when conditions are met. This script should be
 * placed in the <head> of the document as early as possible to make sure profiling starts before
 * other JavaScript execute.
 *
 * Other Notes:
 * - Keeping it old school ECMAScript so it can be used as is in the head.
 * - Keeping comments out of the body of the function as it could be rendered on the client
 * - Some minimal minification done to reduce file size a bit while maintaining readability.
 * - Script execution exits early if client does not support JS Self-Profiler API.
 */
export const jsProfiler = function(options) {
  try {
    if (
      'Profiler' in window &&
      'Scheduler' in window &&
      (window.location.search.includes('e2e_test') || Math.random() <= options.sample_rate)
    ) {
      window.__jsProfiler = new window.Profiler({
        sampleInterval: options.profiler_init_options.sampleInterval || 0,
        maxBufferSize: options.profiler_init_options.maxBufferSize || 10000,
      });
    }
  } catch (err) {}
};

/**
 * @param {Object} [options={}]
 * @param {Object} [options.profiler_init_options={}] - Can include `sampleInterval` and `maxBufferSize`
 * Profiler options. See JS self-Profiling API documentation:
 * http://go.bzfd.it/js-self-profiling-api
 * @param {Object} [options.sample_rate=0.1] - Rate at which to sample profiler events (between 0 and 1)
 * @returns {String} A string representation of the initializing `jsProfiler` function. Can be used
 * for server rendering as an inline script.
 */
export const jsProfilerAsString = ({
  profiler_init_options = {},
  sample_rate = 0.1,
} = {}) => {

  const jsProfilerString = `
    (${jsProfiler.toString()})(${JSON.stringify({ profiler_init_options, sample_rate })});
  `;

  /**
   * Remove any white spaces after the characters ;,{}:
   * Also remove two or more consecutive white spaces
   * https://regex101.com/r/ikukPq/3
   */
  return jsProfilerString.replace(/([;,{}:])\s+|(\s+){2,}/g, '$1$2');
};

/**
 * Find the frame with the most occurences for each provided longtask
 * @param {Array} longtasks - Entries from the Long Task API
 * @param {Object} trace - A profiler trace dictionary provided by the JS Self-Profiling API
 * @param {Object} [options={}]
 * @param {String} option.ignore_pattern - A regex pattern for checking against a resource URL to
 * determine if the frame should be ignored. This may be useful if, for example, you want to ignore
 * library or vendor code.
 * @returns {Array} An array containing arrays of [longtask, frame, resource] for each element.
 */
export const getLongestRunningFrames = (longtasks, trace, {
  // https://regex101.com/r/Ehxbbn/1
  ignore_pattern = 'chunks/(?:framework|main|webpack)',
} = {}) => {
  performance.mark('getLongestRunningFrames_start');
  const results = [];
  const longtask_frame_counts = [];
  const ignore_pattern_regex = new RegExp(ignore_pattern);

  /**
   * Samples are converted to iterators. This allows breaking out of a loop early, and continuing
   * the loop at a later time from where it left off.
   */
  const samples = trace.samples.entries();

  // Iterate over each long task.
  for (const longtask of longtasks) {
    const totals = {};

    // Iterate over each sample sequentially until we reach the start of the longtask timeframe
    for (const [ , sample ] of samples) {
      /**
       * If the sample has an associated stack, AND the sample's timestamp occurs after the
       * long task's start time, then begin to rebuild the stack by walking up from the starting
       * frame.
       */
      if (sample.stackId && sample.timestamp >= longtask.startTime) {
        /**
         * Break out of the loop to continue to the next longtask if sample timestamps are no
         * longer within the current longtask timeframe.
         */
        if (sample.timestamp > longtask.startTime + longtask.duration) {
          break;
        }

        let stack = trace.stacks[sample.stackId];

        while ('parentId' in stack) {
          // Count all occurences of unique frames across all samples of the current long task.
          totals[stack.frameId] = (totals[stack.frameId] || 0) + 1;
          stack = trace.stacks[stack.parentId];
        }
      }
    }

    /**
     * If there are no frame counts then skip this step. Empty counts can occur if nothing was
     * executed at the time the sample was captured.
     */
    if (Object.keys(totals).length) {
      // Along with the frame totals, also save a copy of the longtask associated with these frames
      longtask_frame_counts.push([ longtask, totals ]);
    }
  }

  /**
   * Sort longtask_frame_counts by duration in descending order. This allows prioritising the most
   * impactful frames to report first. Sorting must come AFTER calculating total frames as they need
   * to remain in chronological order while stwpping through profiler samples.
   */
  longtask_frame_counts.sort((a, b) => b[0].duration - a[0].duration);

  /**
   * Find the frame matching all of the criteria. The reducer expects to execute against an array
   * containing arrays of [key, value] for each element, or in this case, arrays of [frame_id,
   * frame_count].
   */
  const longestFrameReducer = (previousValue, currentValue) => {
    /**
     * Skip if count is less than 5. Samples are taken at 10 millisecond intervals. If a frame
     * occurs 5 times, we can estimate the overall time spent was at least 50 milliseconds. This
     * may not be the most accurate approximation due to the limits of sampled profilers. More
     * details in this article:
     * http://go.bzfd.it//downsides-to-sampled-profiling
     */
    if (currentValue[1] < 5) {
      return previousValue;
    }

    /**
     * Skip if the previously qualified frame has a larger number of occurences.
     */
    if (previousValue[1] > currentValue[1]) {
      return previousValue;
    }

    const frame = trace.frames[currentValue[0]];

    /**
     * Skip if there is no associated resourceID. This may occur if the sampled task was garbage
     * collection, style, layout, paint, etc., and not from a javascript resource.
     */
    if (!('resourceId' in frame)) {
      return previousValue;
    }

    const resource = trace.resources[frame.resourceId];

    /**
     * Skip if the ignore pattern provided matches the resource.
     */
    if (ignore_pattern_regex && ignore_pattern_regex.test(resource)) {
      return previousValue;
    }

    return currentValue;
  };

  for (const [ longtask, frame_count_totals ] of longtask_frame_counts) {
    /**
     * Get the frame with the most occurences.
     */
    const [ frame_id ] = Object.entries(frame_count_totals).reduce(longestFrameReducer, []);

    /**
     * `frame_id` will be undefined if no frames match the criteria outlined in the
     * `longestFrameReducer` function.
     */
    if (frame_id) {
      const frame = trace.frames[frame_id];
      const resource = trace.resources[frame.resourceId];
      results.push([ longtask, frame, resource ]);
    }
  }

  performance.measure('getLongestRunningFrames', 'getLongestRunningFrames_start');
  return results;
};

/**
 * Finds the most occuring frame within each longtask and sends a stack trace using the supplied
 * tracking function. Stack traces are made possible using the JS Self-Profiling API. The profiler
 * will be stopped and the metrics will be reported once the First Input Delay (FID) metric is
 * fired.
 * @param {Function} trackPerformanceMetricEvent - A tracking function created with the
 * @buzzfeed/client-event-tracking `createClientEvent` method.
 * @param {Object} [options={}]
 * @param {String} options.ignore_pattern - A regex pattern for checking against a resource URL to
 * determine if the frame should be ignored. This may be useful if, for example, you want to ignore
 * library or vendor code.
 * @param {Number} [options.limit=10] - The max amount of longtasks to report on.
 */
const longtaskLongestFrame = (trackPerformanceMetricEvent, {
  ignore_pattern,
  limit = 10,
} = {}) => {
  onFID(function longtaskLongestFrameFID() {
    /**
     * `__jsProfiler` is defined by the inline <head> script if profiling is enabled and JS
     * Self-Profiling API is supported.
     */
    if ('__jsProfiler' in window) {
      /**
       * On a Moto G4, awaiting the profiler's `stop()` method clocks in at about 100ms and
       * processing the profiler data can also reach > 50ms. To help prevent a bad user experience,
       * the function uses Priority Task Scheduling API:
       * @see http://go.bzfd.it/porioritized-task-scheduling-api
       */
      window.scheduler.postTask(async() => {
        performance.mark('__jsProfiler_start');
        const trace = await window.__jsProfiler.stop();
        performance.measure('__jsProfiler', '__jsProfiler_start');

        const observer = new PerformanceObserver(async function longtaskLongestFrameObserver(list) {
          observer.disconnect();

          const longtasks = list.getEntries();

          const longest_running_frames = getLongestRunningFrames(longtasks, trace, {
            ignore_pattern,
          });

          for (const [ longtask, frame, resource ] of longest_running_frames) {
            if (!(--limit)) {
              break;
            }

            trackPerformanceMetricEvent({
              metric_name: 'longtask-longest-frame',
              metric_type: 'custom',
              metric_value: longtask.duration,
              metric_metadata_type: 'stacktrace',
              metric_metadata_value: `${resource}:${frame.line}:${frame.column}`,
            });
          }
        });

        observer.observe({type: 'longtask', buffered: true});
      }, { priority: 'background' });
    }
  });
};

export default longtaskLongestFrame;
